import { supabase } from "./client";

// Tipos de arquivo suportados
const SUPPORTED_FILE_TYPES = {
  documents: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  images: ['image/jpeg', 'image/png', 'image/webp'],
  sheets: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
};

const SUPPORTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'webp', 'xls', 'xlsx'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function validateFile(file: File): { valid: boolean; error?: string } {
  // Validar tamanho
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `Arquivo muito grande! O tamanho máximo é 10MB, e você enviou ${(file.size / 1024 / 1024).toFixed(2)}MB.`
    };
  }

  // Validar tipo MIME
  const allSupportedTypes = [
    ...SUPPORTED_FILE_TYPES.documents,
    ...SUPPORTED_FILE_TYPES.images,
    ...SUPPORTED_FILE_TYPES.sheets,
  ];

  if (!allSupportedTypes.includes(file.type)) {
    const extension = file.name.split('.').pop()?.toLowerCase() || 'desconhecido';

    if (!SUPPORTED_EXTENSIONS.includes(extension)) {
      return {
        valid: false,
        error: `Tipo de arquivo não suportado: .${extension}\n\nFormatos aceitos:\n• 📄 PDF\n• 📝 Word (DOC, DOCX)\n• 🖼️ Imagens (JPG, PNG, WEBP)\n• 📊 Excel (XLS, XLSX)`
      };
    }
  }

  return { valid: true };
}

export async function uploadOrderFile(file: File, orderCode: string): Promise<{ ok: boolean; url?: string; error?: string }> {
  try {
    console.log("Iniciando upload do arquivo:", file.name, "para pedido:", orderCode);

    // Validar arquivo antes de enviar
    const validation = validateFile(file);
    if (!validation.valid) {
      console.warn("Validação de arquivo falhou:", validation.error);
      return { ok: false, error: validation.error };
    }

    // Verificar se o usuário está autenticado
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("Usuário não autenticado");
      return { ok: false, error: "Você não está autenticado. Faça login novamente." };
    }

    const bucket = "orders";
    const path = `${orderCode}/${Date.now()}-${file.name}`;

    console.log("Upload para bucket:", bucket, "path:", path);
    console.log("Usuário autenticado:", user.id);

    const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
      upsert: true,
      cacheControl: "3600"
    });

    if (error) {
      const errorDetails = error as unknown as { message?: string; statusCode?: number; error?: string };
      console.error("Erro no upload:", error);
      console.error("Detalhes do erro:", {
        message: errorDetails?.message,
        statusCode: errorDetails?.statusCode,
        error: errorDetails?.error
      });

      // Se for erro de RLS ou bucket não existe, retornar erro mas não falhar o pedido
      if (error.message.includes('row-level security policy') ||
          error.message.includes('Bucket not found') ||
          error.message.includes('not found') ||
          error.message.includes('permission denied') ||
          error.message.includes('unauthorized')) {
        console.log("Bucket não configurado ou erro de RLS, continuando sem arquivo");
        return { ok: false, error: "⚠️ Não conseguimos salvar o arquivo no momento.\n\nSeu pedido foi criado com sucesso, mas o arquivo não foi anexado. Você pode tentar novamente depois." };
      }

      throw error;
    }

    console.log("Upload realizado com sucesso:", data);

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    console.log("URL pública gerada:", urlData.publicUrl);

    return { ok: true, url: urlData.publicUrl };
  } catch (e: unknown) {
    console.error("Erro no upload:", e);
    const errorMessage = e instanceof Error ? e.message : "Falha no upload";
    return { ok: false, error: `❌ Erro ao enviar arquivo: ${errorMessage}\n\nSeu pedido será criado, mas o arquivo não foi anexado.` };
  }
}

export async function uploadProductImage(file: File, productId: string): Promise<{ ok: boolean; url?: string; error?: string }> {
  try {
    console.log("Iniciando upload da imagem do produto:", file.name, "para produto:", productId);
    
    // Verificar se o usuário está autenticado
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("Usuário não autenticado");
      return { ok: false, error: "Usuário não autenticado" };
    }
    
    const bucket = "products";
    const path = `${productId}/${Date.now()}-${file.name}`;
    
    console.log("Upload para bucket:", bucket, "path:", path);
    
    // Tentar fazer upload diretamente (mais eficiente que verificar antes)
    const { data, error } = await supabase.storage.from(bucket).upload(path, file, { 
      upsert: true, 
      cacheControl: "3600" 
    });
    
    if (error) {
      console.error("Erro no upload:", error);
      
      // Se for erro de RLS ou bucket não existe, retornar erro
      if (error.message.includes('row-level security policy') || 
          error.message.includes('Bucket not found') ||
          error.message.includes('not found') ||
          error.message.includes('permission denied') ||
          error.message.includes('unauthorized')) {
        console.log("Erro de permissão ou bucket não configurado");
        return { ok: false, error: "Upload não disponível - verifique as configurações de armazenamento" };
      }
      
      throw error;
    }
    
    console.log("Upload realizado com sucesso:", data);
    
    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    console.log("URL pública gerada:", urlData.publicUrl);
    
    return { ok: true, url: urlData.publicUrl };
  } catch (e: unknown) {
    console.error("Erro no upload:", e);
    return { ok: false, error: e instanceof Error ? e.message : "Falha no upload" };
  }
}

export async function uploadCompanyLogo(file: File, empresaId: string): Promise<{ ok: boolean; url?: string; error?: string }> {
  try {
    console.log("Iniciando upload do logo da empresa:", file.name, "para empresa:", empresaId);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("Usuário não autenticado");
      return { ok: false, error: "Usuário não autenticado" };
    }

    const bucket = "logos";
    const path = `${empresaId}/${Date.now()}-${file.name}`;

    console.log("Upload para bucket:", bucket, "path:", path);

    const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
      upsert: true,
      cacheControl: "3600"
    });

    if (error) {
      console.error("Erro no upload:", error);

      if (
        error.message.includes('row-level security policy') ||
        error.message.includes('Bucket not found') ||
        error.message.includes('not found') ||
        error.message.includes('permission denied') ||
        error.message.includes('unauthorized')
      ) {
        console.log("Erro de permissão ou bucket não configurado");
        return { ok: false, error: "Upload não disponível - verifique as configurações de armazenamento" };
      }

      throw error;
    }

    console.log("Upload realizado com sucesso:", data);

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    console.log("URL pública gerada:", urlData.publicUrl);

    return { ok: true, url: urlData.publicUrl };
  } catch (e: unknown) {
    console.error("Erro no upload:", e);
    return { ok: false, error: e instanceof Error ? e.message : "Falha no upload" };
  }
}

export async function uploadQuoteFile(file: File, quoteCode: string): Promise<{ ok: boolean; url?: string; error?: string }> {
  try {
    console.log("Iniciando upload do arquivo:", file.name, "para orçamento:", quoteCode);

    // Validar arquivo antes de enviar
    const validation = validateFile(file);
    if (!validation.valid) {
      console.warn("Validação de arquivo falhou:", validation.error);
      return { ok: false, error: validation.error };
    }

    // Verificar se o usuário está autenticado
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error("Usuário não autenticado");
      return { ok: false, error: "Você não está autenticado. Faça login novamente." };
    }

    const bucket = "quotes";
    const path = `${quoteCode}/${Date.now()}-${file.name}`;

    console.log("Upload para bucket:", bucket, "path:", path);
    console.log("Usuário autenticado:", user.id);

    const { data, error } = await supabase.storage.from(bucket).upload(path, file, {
      upsert: true,
      cacheControl: "3600"
    });

    if (error) {
      const errorDetails = error as unknown as { message?: string; statusCode?: number; error?: string };
      console.error("Erro no upload:", error);
      console.error("Detalhes do erro:", {
        message: errorDetails?.message,
        statusCode: errorDetails?.statusCode,
        error: errorDetails?.error
      });

      // Se for erro de RLS ou bucket não existe, retornar erro mas não falhar o orçamento
      if (error.message.includes('row-level security policy') ||
          error.message.includes('Bucket not found') ||
          error.message.includes('not found') ||
          error.message.includes('permission denied') ||
          error.message.includes('unauthorized')) {
        console.log("Bucket não configurado ou erro de RLS, continuando sem arquivo");
        return { ok: false, error: "⚠️ Não conseguimos salvar o arquivo no momento.\n\nSeu orçamento foi criado com sucesso, mas o arquivo não foi anexado. Você pode tentar novamente depois." };
      }

      throw error;
    }

    console.log("Upload realizado com sucesso:", data);

    const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(path);
    console.log("URL pública gerada:", urlData.publicUrl);

    return { ok: true, url: urlData.publicUrl };
  } catch (e: unknown) {
    console.error("Erro no upload:", e);
    const errorMessage = e instanceof Error ? e.message : "Falha no upload";
    return { ok: false, error: `❌ Erro ao enviar arquivo: ${errorMessage}\n\nSeu orçamento será criado, mas o arquivo não foi anexado.` };
  }
}


